import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  deleteNominationA2Board,
  deleteNominationImage,
  downloadAllNominationImages,
  downloadNominationImage,
  getAuditLog,
  getNominationImages,
  listNominations,
  replaceNominationImage,
  setNominationShortlisted,
  updateImageCredit,
  updateNomination,
  uploadNominationA2Board,
  type AuditEntry,
  type ImageInfo,
  type Nomination,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { WAF_YELLOW_DARK } from '../theme';
import { AtAGlance } from '../components/AtAGlance';
import { LoadingScreen } from '../components/LoadingScreen';
import { A2BoardsPreview, VideoPreview } from '../components/EntryMedia';
import { a2BoardsFrom } from '../lib/entryMedia';
import { TermMultiSelect, WbdFieldInput } from '../components/TermPickers';
import {
  PRIZE_SEPARATOR,
  SHORTLIST,
  STATUS_SEPARATOR,
  joinValues,
  prizeStatusOptions,
  specialPrizeOptions,
  splitValues,
  statusOptions,
  useTaxonomies,
  wafxOptions,
  type TermOption,
} from '../lib/taxonomies';

// Shared button styles — WAF yellow accents per design system.
const primaryButtonSx = {
  backgroundColor: WAF_YELLOW_DARK,
  color: 'text.primary',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#E6B800' },
  '&.Mui-disabled': { backgroundColor: '#F5D028', color: 'rgba(0,0,0,0.38)' },
};
const outlinedYellowSx = {
  borderColor: WAF_YELLOW_DARK,
  borderWidth: 2,
  color: 'text.primary',
  fontWeight: 600,
  '& .MuiSvgIcon-root': { color: 'text.primary' },
  '&:hover': {
    borderColor: '#E6B800',
    borderWidth: 2,
    backgroundColor: 'rgba(255, 204, 0, 0.08)',
  },
};

const MAX_MEDIA_BYTES = 40 * 1024 * 1024; // 40 MB — matches the server cap.
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

// Only the canonical slots (hero, img2..img10) can be replaced through the admin
// endpoint — guard the Replace button so it never targets an unexpected type.
const canReplaceSlot = (type: string) => /^(hero|img([2-9]|10))$/.test(type);

// The canonical non-hero gallery slots, in order. The gallery renders EVERY one
// of these — a filled slot shows its image + controls, an empty slot shows an
// "Add image" dropzone — so staff can populate the full set and re-add after a
// delete. Matches EvessioJsonParser.ImageFields / NominationImage.ImageType.
const GALLERY_SLOTS = ['img2', 'img3', 'img4', 'img5', 'img6', 'img7', 'img8', 'img9', 'img10'] as const;
const slotLabel = (slot: string) => `Image ${slot.replace('img', '')}`;

// A dashed "Add image" affordance shown in an empty slot (hero or gallery). Uses
// the very same upload path as Replace — POSTing to an empty slot upserts the row
// — so there's no separate "add" endpoint. Fixed height so empty slots line up
// with filled tiles in the grid.
function AddImageDropzone({
  label,
  height,
  busy,
  disabled,
  onPick,
  onError,
}: {
  label: string;
  height: number | Record<string, number>;
  busy: boolean;
  disabled: boolean;
  onPick: (file: File) => void;
  onError: (message: string) => void;
}) {
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        px: 1,
      }}
    >
      <MediaUploadButton
        label={label}
        accept="image/jpeg,image/png"
        acceptTypes={ACCEPTED_IMAGE_TYPES}
        busy={busy}
        disabled={disabled}
        onPick={onPick}
        onError={onError}
        sx={outlinedYellowSx}
      />
    </Box>
  );
}

// A small "Replace" / "Upload" file-picker button used for both image slots and
// A2 board PDFs. Validates type + size client-side (the server re-validates by
// content-type + magic bytes) and hands a good file to `onPick`.
function MediaUploadButton({
  label,
  accept,
  acceptTypes,
  busy,
  disabled,
  onPick,
  onError,
  sx,
}: {
  label: string;
  accept: string;
  acceptTypes: string[];
  busy: boolean;
  disabled: boolean;
  onPick: (file: File) => void;
  onError: (message: string) => void;
  sx?: object;
}) {
  return (
    <Button
      component="label"
      variant="outlined"
      size="small"
      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
      disabled={disabled}
      sx={sx}
    >
      {busy ? 'Uploading…' : label}
      <input
        hidden
        type="file"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (acceptTypes.length > 0 && !acceptTypes.includes(file.type)) {
            onError(`${file.name}: unsupported file type.`);
            return;
          }
          if (file.size > MAX_MEDIA_BYTES) {
            onError(`${file.name}: file is over the 40 MB limit.`);
            return;
          }
          onPick(file);
        }}
      />
    </Button>
  );
}

// A small "Remove" (delete) icon button shown next to the Replace / Upload
// control for staff. Opens a confirmation dialog via `onClick` (the parent owns
// the dialog); shows a spinner while its own delete is in flight.
function MediaDeleteButton({
  busy,
  disabled,
  onClick,
  tooltip,
}: {
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size="small"
          color="error"
          onClick={onClick}
          disabled={disabled}
          aria-label={tooltip}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// Format an audit value for display. Unwraps JSON-encoded scalars and trims.
function formatAuditValue(v: AuditEntry['value_before']): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v === '' ? '(empty)' : v;
  return String(v);
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function prettyField(field: string | null): string {
  if (!field) return '(whole entry)';
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettySource(source: string): string {
  if (source === 'manual') return 'Manual edit';
  if (source === 'evessio_sync') return 'Evessio sync';
  if (source.startsWith('change_request:')) return `Change request #${source.split(':')[1]}`;
  return source;
}

const EDITABLE_FIELDS: Array<{ key: keyof Nomination; label: string; multiline?: boolean }> = [
  { key: 'project_title', label: 'Project title' },
  { key: 'nomination_code', label: 'Project number' },
  { key: 'category_name', label: 'Category' },
  // Statuses and prizes: multi-selects of WBD terms, rendered in the "Status &
  // awards" block rather than the generic field list. "Shortlist" is the one
  // status not stored in category_status — it's the shortlisted_at flag.
  { key: 'category_status', label: 'Status' },
  { key: 'special_prizes', label: 'Special prizes' },
  { key: 'wafx_awards', label: 'WAFX awards' },
  { key: 'prize_statuses', label: 'Prize status' },
  { key: 'project_city', label: 'City' },
  { key: 'project_country', label: 'Country' },
  { key: 'architect_company_name', label: 'Architect company' },
  { key: 'architect_company_country', label: 'Architect company country' },
  { key: 'lead_architect_name', label: 'Lead architect' },
  { key: 'client_name', label: 'Client' },
  { key: 'project_completion_date', label: 'Completion date' },
  { key: 'google_maps_link', label: 'Google Maps link' },
  { key: 'project_summary', label: 'Project summary', multiline: true },
  // A2 boards are upload-only now (PDF → our storage), not a free-text URL — the
  // a2board1/a2board2 fields are handled by the upload buttons in the A2 section
  // below, not the generic text edit form.
  { key: 'video_landscape_url', label: 'Landscape video link' },
  { key: 'video_portrait_url', label: 'Portrait video link' },
  { key: 'video_download_url', label: 'Video download link' },
  { key: 'video_copyright', label: 'Video copyright', multiline: true },
];

function SectionCard({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {actions}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Paper>
  );
}

// A compact, always-visible download control overlaid on an image's top-right
// corner. Semi-opaque dark pill so it reads against any photo. Shows a spinner
// while its own download is in flight.
function ImageDownloadButton({
  onClick,
  busy,
  disabled,
}: {
  onClick: () => void;
  busy: boolean;
  disabled: boolean;
}) {
  return (
    <Tooltip title="Download image">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          aria-label="Download image"
          sx={{
            m: 0.5,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.55)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
          }}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// Inline credit editor shown under each image. Read-only "© {credit}" for
// non-editors (and PR); staff (canEdit) get an edit pencil that swaps in a small
// text field and saves directly via PATCH .../images/{id}/credit (audit-logged
// server-side). Entrants edit credits through their own change-request view
// (SubmitChange), not here — this page is staff/PR only. (NOC-397)
function ImageCreditEditor({
  uuid,
  img,
  canEdit,
  onSaved,
}: {
  uuid: string;
  img: ImageInfo;
  canEdit: boolean;
  onSaved: (id: number, credit: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const start = () => {
    setDraft(img.credit ?? '');
    setErr(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErr(null);
  };
  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await updateImageCredit(uuid, img.id, draft.trim());
      onSaved(img.id, res.credit ?? '');
      setEditing(false);
    } catch (e: unknown) {
      const x = e as { response?: { data?: { error?: string } }; message?: string };
      setErr(x.response?.data?.error || x.message || 'Failed to save credit');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        <TextField
          size="small"
          variant="standard"
          placeholder="Photographer / source"
          value={draft}
          autoFocus
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') cancel();
          }}
          sx={{ maxWidth: 280 }}
        />
        <IconButton size="small" onClick={() => void save()} disabled={saving} aria-label="Save credit">
          {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={cancel} disabled={saving} aria-label="Cancel credit edit">
          <CloseIcon fontSize="small" />
        </IconButton>
        {err && (
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
      {img.credit ? (
        <Typography variant="caption" color="text.secondary">
          © {img.credit}
        </Typography>
      ) : (
        canEdit && (
          <Typography variant="caption" color="text.disabled">
            No credit
          </Typography>
        )
      )}
      {canEdit && (
        <Tooltip title="Edit credit">
          <IconButton size="small" onClick={start} aria-label="Edit credit" sx={{ p: 0.25 }}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

// Read-only chips for a multi-valued WBD field ("—" when empty).
function TermChips({ values, highlight }: { values: string[]; highlight?: string }) {
  if (values.length === 0)
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {values.map((v) => (
        <Chip key={v} size="small" label={v} color={v === highlight ? 'success' : 'default'} />
      ))}
    </Stack>
  );
}

export function EntryDetail() {
  const { uuid = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasRole } = useAuth();
  // Read-only PR role: no edit affordances and no History (audit is staff-only
  // and may contain PII) — but PR sees the same full entry data as staff.
  const canEdit = hasRole('Admin') || hasRole('User');
  // "Can view → can download": everyone who can see this entry's images can
  // download them, matching the server-side authorization on the download
  // endpoints. This page is only reachable by Admin/User/PR (entrants use their
  // own view), and all of them can view the images, so all of them get the
  // download controls.
  const canDownloadImages = hasRole('Admin') || hasRole('User') || hasRole('PR');
  // A2 board + video fields are editable, but rendered inside their own display
  // sections below (next to the boards/videos) rather than in the generic
  // details form — so exclude them here. They stay in EDITABLE_FIELDS so the
  // draft-seed + save logic still picks them up.
  const RELOCATED_FIELD_KEYS = new Set<keyof Nomination>([
    'video_landscape_url', 'video_portrait_url', 'video_download_url', 'video_copyright',
    'category_status', 'special_prizes', 'wafx_awards', 'prize_statuses',
  ]);
  const visibleFields = EDITABLE_FIELDS.filter((f) => !RELOCATED_FIELD_KEYS.has(f.key));
  // Grid position of the "Status & awards" block: straight after Google Maps
  // link. Fields get even CSS `order`s, the block the odd one after that field.
  const awardsOrder = visibleFields.findIndex((f) => f.key === 'google_maps_link') * 2 + 1;
  const [nomination, setNomination] = useState<Nomination | null>(null);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [editing, setEditing] = useState<boolean>(canEdit && searchParams.get('edit') === '1');
  const [draft, setDraft] = useState<Partial<Nomination>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which image download is in flight: 'all' for the zip, an image id for a
  // single image, or null when idle. Drives the spinner + disabled state.
  const [downloading, setDownloading] = useState<number | 'all' | null>(null);
  const [shortlistSaving, setShortlistSaving] = useState(false);
  // WBD term lists for the dropdowns (null until loaded → plain text inputs).
  const tax = useTaxonomies();
  // In edit mode "Shortlist" sits in the status multi-select, but it's saved
  // through the shortlisted_at flag, so the draft tracks it separately.
  const [draftShortlisted, setDraftShortlisted] = useState(false);
  // Which slot's image replace / A2 upload is in flight (slot key, or null).
  const [replacingSlot, setReplacingSlot] = useState<string | null>(null);
  const [a2UploadingSlot, setA2UploadingSlot] = useState<string | null>(null);
  // Which slot's delete is in flight (slot key, or null).
  const [deletingSlot, setDeletingSlot] = useState<string | null>(null);
  // The pending delete awaiting confirmation, or null when the dialog is closed.
  const [confirmDelete, setConfirmDelete] = useState<
    { kind: 'image' | 'a2board'; slot: string; label: string } | null
  >(null);
  // The image shown enlarged in the click-to-zoom lightbox, or null when closed.
  const [lightbox, setLightbox] = useState<ImageInfo | null>(null);

  const reloadAudit = () => {
    getAuditLog('nomination', uuid).then(setAuditLog).catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listNominations(),
      getNominationImages(uuid),
      // Audit history is staff-only (RequireStaff) and can carry PII in its
      // before/after values, so PR users neither fetch nor see it. Fetching it
      // would 403 and reject the whole Promise.all, blanking the page.
      canEdit ? getAuditLog('nomination', uuid) : Promise.resolve<AuditEntry[]>([]),
    ])
      .then(([all, imgs, audit]) => {
        if (cancelled) return;
        const found = all.find((n) => n.nomination_uuid === uuid) || null;
        setNomination(found);
        setImages(imgs);
        setAuditLog(audit);
        // Direct-edit entry-point: when we land here with `?edit=1` in the
        // URL (e.g. clicking the pencil on the entries table) `editing` is
        // already true from the initial state, but the draft hasn't been
        // hydrated from the nomination — `startEdit()` is what does that
        // and it only fires on user click. Without this, the edit form
        // renders with all fields blank. Mirror startEdit's logic here.
        if (found && canEdit && searchParams.get('edit') === '1') {
          const d: Partial<Nomination> = {};
          EDITABLE_FIELDS.forEach(({ key }) => {
            d[key] = found[key] as string;
          });
          setDraft(d);
          setDraftShortlisted(!!found.shortlisted_at);
        }
      })
      .catch((err) => !cancelled && setError(err.message || String(err)));
    return () => {
      cancelled = true;
    };
    // searchParams intentionally excluded — we only want to hydrate the
    // draft on the initial nomination load. Toggling edit via the in-page
    // button updates searchParams and would otherwise cause this effect
    // to re-run and clobber any in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const startEdit = () => {
    if (!nomination) return;
    const d: Partial<Nomination> = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      d[key] = nomination[key] as string;
    });
    setDraft(d);
    setDraftShortlisted(!!nomination.shortlisted_at);
    setEditing(true);
    setSearchParams({ edit: '1' });
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
    setError(null);
    setSearchParams({});
  };

  const save = async () => {
    if (!nomination) return;
    setSaving(true);
    setError(null);
    try {
      // Send only changed fields
      const patch: Partial<Nomination> = {};
      EDITABLE_FIELDS.forEach(({ key }) => {
        if (draft[key] !== undefined && draft[key] !== nomination[key]) {
          patch[key] = draft[key];
        }
      });
      const shortlistChanged = draftShortlisted !== !!nomination.shortlisted_at;
      if (Object.keys(patch).length === 0 && !shortlistChanged) {
        cancelEdit();
        return;
      }
      let updated = nomination;
      if (Object.keys(patch).length > 0) updated = await updateNomination(uuid, patch);
      if (shortlistChanged) updated = await setNominationShortlisted(uuid, draftShortlisted);
      setNomination(updated);
      setEditing(false);
      setDraft({});
      setSearchParams({});
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadImage = async (imageId: number) => {
    setDownloading(imageId);
    setError(null);
    try {
      await downloadNominationImage(uuid, imageId);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Image download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading('all');
    setError(null);
    try {
      await downloadAllNominationImages(uuid);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Image download failed');
    } finally {
      setDownloading(null);
    }
  };

  const toggleShortlisted = async (next: boolean) => {
    if (!nomination) return;
    setShortlistSaving(true);
    setError(null);
    try {
      const updated = await setNominationShortlisted(uuid, next);
      setNomination(updated);
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Failed to update shortlist status');
    } finally {
      setShortlistSaving(false);
    }
  };

  // Patch the just-saved credit into local image state so the "© …" line
  // updates without a full reload.
  const handleCreditSaved = (id: number, credit: string) => {
    setImages((prev) => prev.map((im) => (im.id === id ? { ...im, credit: credit || null } : im)));
  };

  // Staff immediate image replace (Admin/User). Applies at once, then re-fetches
  // the image list so the swapped slot picks up its new versioned (cache-busted)
  // URL. `slot` is the image's type (hero, img2..img10).
  const handleReplaceImage = async (slot: string, file: File) => {
    setReplacingSlot(slot);
    setError(null);
    try {
      await replaceNominationImage(uuid, slot, file);
      const imgs = await getNominationImages(uuid);
      setImages(imgs);
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Image replace failed');
    } finally {
      setReplacingSlot(null);
    }
  };

  // Staff A2 board PDF upload (Admin/User). Hosts the PDF on our storage and
  // repoints the board field at the returned served URL.
  const handleUploadA2Board = async (slot: 'a2board1' | 'a2board2', file: File) => {
    setA2UploadingSlot(slot);
    setError(null);
    try {
      const res = await uploadNominationA2Board(uuid, slot, file);
      setNomination((n) => (n ? { ...n, [slot]: res.url } : n));
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'A2 board upload failed');
    } finally {
      setA2UploadingSlot(null);
    }
  };

  // Staff immediate image delete (Admin/User). Archives to history server-side,
  // then clears the slot; re-fetches the image list so the removed slot disappears.
  const handleDeleteImage = async (slot: string) => {
    setDeletingSlot(slot);
    setError(null);
    try {
      await deleteNominationImage(uuid, slot);
      const imgs = await getNominationImages(uuid);
      setImages(imgs);
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Image delete failed');
    } finally {
      setDeletingSlot(null);
    }
  };

  // Staff immediate A2 board delete (Admin/User). Removes the stored PDF and clears
  // the board field so the "Open A2 board" link disappears.
  const handleDeleteA2Board = async (slot: 'a2board1' | 'a2board2') => {
    setDeletingSlot(slot);
    setError(null);
    try {
      await deleteNominationA2Board(uuid, slot);
      setNomination((n) => (n ? { ...n, [slot]: null } : n));
      reloadAudit();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'A2 board delete failed');
    } finally {
      setDeletingSlot(null);
    }
  };

  // Run the delete the confirmation dialog is asking about, then close it.
  const runConfirmedDelete = async () => {
    if (!confirmDelete) return;
    const { kind, slot } = confirmDelete;
    setConfirmDelete(null);
    if (kind === 'image') await handleDeleteImage(slot);
    else await handleDeleteA2Board(slot as 'a2board1' | 'a2board2');
  };

  const heroImage = useMemo(() => images.find((i) => i.type === 'hero'), [images]);
  // Build the gallery as the FULL canonical slot set so empty slots render an
  // "Add image" control, not just the slots that already have an image.
  const gallerySlots = useMemo(
    () => GALLERY_SLOTS.map((slot) => ({ slot, image: images.find((i) => i.type === slot) ?? null })),
    [images],
  );
  // Any image whose type isn't a canonical slot (legacy / unexpected data) is
  // shown too so it's never silently hidden — download only, no slot controls.
  const strayImages = useMemo(() => {
    const known = new Set<string>(['hero', ...GALLERY_SLOTS]);
    return images.filter((i) => !known.has(i.type));
  }, [images]);

  const a2Boards = useMemo(() => (nomination ? a2BoardsFrom(nomination) : []), [nomination]);

  if (!nomination && !error) {
    return <LoadingScreen />;
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Button
          variant="text"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/entries"
          sx={{ color: 'text.secondary', pl: 0 }}
        >
          Back to all entries
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {nomination && (
        <>
          <AtAGlance nomination={nomination} />
          <SectionCard
            title="All Metadata"
            actions={
              !canEdit ? undefined : editing ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={cancelEdit}
                    disabled={saving}
                    sx={{ ...outlinedYellowSx, flex: { xs: 1, sm: 'none' } }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<CheckIcon />}
                    onClick={save}
                    disabled={saving}
                    sx={{ ...primaryButtonSx, flex: { xs: 1, sm: 'none' } }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={startEdit}
                  sx={outlinedYellowSx}
                >
                  Edit
                </Button>
              )
            }
          >
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="h5">{nomination.project_title || '(Untitled)'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {nomination.architect_company_name || '—'}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                columnGap: 3,
                rowGap: 1.5,
              }}
            >
              {/* Status & awards: multi-selects of the WBD taxonomy terms. Status
                  "Shortlist" is the shortlisted_at flag (NOC-380); the rest are
                  stored in category_status. Staff (Admin + emap User) edit these;
                  PR and entrants only see them. Placed after Google Maps link
                  via `order` (see awardsOrder). */}
              <Box sx={{ gridColumn: { sm: '1 / -1' }, order: awardsOrder }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    columnGap: 3,
                    rowGap: 1.5,
                  }}
                >
                  <Stack spacing={0.25}>
                    <Stack direction="row" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      {canEdit && !editing && (
                        <FormControlLabel
                          sx={{ ml: 'auto', mr: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={!!nomination.shortlisted_at}
                              disabled={shortlistSaving}
                              onChange={(e) => toggleShortlisted(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" color="text.secondary">
                              Shortlisted
                            </Typography>
                          }
                        />
                      )}
                    </Stack>
                    {editing ? (
                      <TermMultiSelect
                        value={[
                          ...splitValues(draft.category_status as string, STATUS_SEPARATOR).filter((v) => v !== SHORTLIST),
                          ...(draftShortlisted ? [SHORTLIST] : []),
                        ]}
                        options={tax ? statusOptions(tax) : []}
                        onChange={(vals) => {
                          setDraftShortlisted(vals.includes(SHORTLIST));
                          setDraft((d) => ({
                            ...d,
                            category_status: joinValues(vals.filter((v) => v !== SHORTLIST), STATUS_SEPARATOR),
                          }));
                        }}
                      />
                    ) : (
                      <TermChips
                        values={[
                          ...splitValues(nomination.category_status, STATUS_SEPARATOR).filter((v) => v !== SHORTLIST),
                          ...(nomination.shortlisted_at ? [SHORTLIST] : []),
                        ]}
                        highlight={SHORTLIST}
                      />
                    )}
                  </Stack>
                  {(
                    [
                      ['special_prizes', 'Special prizes', tax ? specialPrizeOptions(tax) : []],
                      ['wafx_awards', 'WAFX awards', tax ? wafxOptions(tax) : []],
                      ['prize_statuses', 'Prize status', tax ? prizeStatusOptions(tax) : []],
                    ] as Array<[keyof Nomination, string, TermOption[]]>
                  ).map(([key, label, options]) => (
                    <Stack key={key} spacing={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      {editing ? (
                        <TermMultiSelect
                          value={splitValues(draft[key] as string, PRIZE_SEPARATOR)}
                          options={options}
                          onChange={(vals) =>
                            setDraft((d) => ({ ...d, [key]: joinValues(vals, PRIZE_SEPARATOR) }))
                          }
                        />
                      ) : (
                        <TermChips values={splitValues(nomination[key] as string, PRIZE_SEPARATOR)} />
                      )}
                    </Stack>
                  ))}
                </Box>
              </Box>
              {visibleFields.map(({ key, label, multiline }, i) => (
                <Stack
                  key={key}
                  spacing={0.25}
                  sx={{ gridColumn: multiline ? { sm: '1 / -1' } : undefined, order: i * 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  {editing ? (
                    <WbdFieldInput
                      field={String(key)}
                      multiline={multiline}
                      value={(draft[key] as string) ?? ''}
                      onChange={(v) => setDraft((d) => ({ ...d, [key]: v }))}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: multiline ? 'pre-wrap' : undefined,
                        color: nomination[key] ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {(nomination[key] as string) || '—'}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Box>
          </SectionCard>

          <SectionCard
            title={`Images (${images.length})`}
            actions={
              canDownloadImages && images.length > 0 ? (
                <Button
                  variant="outlined"
                  startIcon={
                    downloading === 'all' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadAll}
                  disabled={downloading !== null}
                  sx={outlinedYellowSx}
                >
                  {downloading === 'all' ? 'Preparing…' : 'Download all'}
                </Button>
              ) : undefined
            }
          >
            {images.length === 0 && !canEdit ? (
              <Typography variant="body2" color="text.secondary">
                No images attached.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {/* ── Hero ─────────────────────────────────────────────── */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Hero image
                  </Typography>
                  {heroImage ? (
                    <Box sx={{ maxWidth: 720 }}>
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={heroImage.url}
                          alt="Hero"
                          onClick={() => setLightbox(heroImage)}
                          sx={{
                            width: '100%',
                            height: { xs: 220, sm: 340 },
                            objectFit: 'contain',
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'block',
                            cursor: 'zoom-in',
                          }}
                        />
                        {canDownloadImages && (
                          <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                            <ImageDownloadButton
                              onClick={() => handleDownloadImage(heroImage.id)}
                              busy={downloading === heroImage.id}
                              disabled={downloading !== null}
                            />
                          </Box>
                        )}
                      </Box>
                      <ImageCreditEditor
                        uuid={uuid}
                        img={heroImage}
                        canEdit={canEdit}
                        onSaved={handleCreditSaved}
                      />
                      {canEdit && canReplaceSlot(heroImage.type) && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                          <MediaUploadButton
                            label="Replace image"
                            accept="image/jpeg,image/png"
                            acceptTypes={ACCEPTED_IMAGE_TYPES}
                            busy={replacingSlot === 'hero'}
                            disabled={replacingSlot !== null || deletingSlot !== null}
                            onPick={(f) => handleReplaceImage('hero', f)}
                            onError={setError}
                            sx={outlinedYellowSx}
                          />
                          <MediaDeleteButton
                            tooltip="Remove image"
                            busy={deletingSlot === 'hero'}
                            disabled={replacingSlot !== null || deletingSlot !== null}
                            onClick={() =>
                              setConfirmDelete({ kind: 'image', slot: 'hero', label: 'main (hero) image' })
                            }
                          />
                        </Stack>
                      )}
                    </Box>
                  ) : canEdit ? (
                    <Box sx={{ maxWidth: 720 }}>
                      <AddImageDropzone
                        label="Add hero image"
                        height={{ xs: 220, sm: 340 }}
                        busy={replacingSlot === 'hero'}
                        disabled={replacingSlot !== null || deletingSlot !== null}
                        onPick={(f) => handleReplaceImage('hero', f)}
                        onError={setError}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No hero image.
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* ── Gallery ──────────────────────────────────────────── */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Gallery
                  </Typography>
                  {(() => {
                    // Staff see every slot (empties get an Add control); PR / other
                    // viewers see only the filled ones.
                    const tiles = canEdit ? gallerySlots : gallerySlots.filter((t) => t.image);
                    if (tiles.length === 0 && strayImages.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          No gallery images.
                        </Typography>
                      );
                    }
                    return (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(4, 1fr)',
                          },
                          gap: 1.5,
                        }}
                      >
                        {tiles.map(({ slot, image }) =>
                          image ? (
                            <Box key={slot} sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ position: 'relative' }}>
                                <Box
                                  component="img"
                                  src={image.url}
                                  alt={image.credit ? `${slotLabel(slot)} — © ${image.credit}` : slotLabel(slot)}
                                  loading="lazy"
                                  onClick={() => setLightbox(image)}
                                  sx={{
                                    width: '100%',
                                    height: { xs: 150, sm: 170 },
                                    objectFit: 'contain',
                                    bgcolor: 'background.default',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    display: 'block',
                                    cursor: 'zoom-in',
                                  }}
                                />
                                {canDownloadImages && (
                                  <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                                    <ImageDownloadButton
                                      onClick={() => handleDownloadImage(image.id)}
                                      busy={downloading === image.id}
                                      disabled={downloading !== null}
                                    />
                                  </Box>
                                )}
                              </Box>
                              <Box sx={{ px: 0.25 }}>
                                <ImageCreditEditor
                                  uuid={uuid}
                                  img={image}
                                  canEdit={canEdit}
                                  onSaved={handleCreditSaved}
                                />
                                {canEdit && canReplaceSlot(slot) && (
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                    <MediaUploadButton
                                      label="Replace"
                                      accept="image/jpeg,image/png"
                                      acceptTypes={ACCEPTED_IMAGE_TYPES}
                                      busy={replacingSlot === slot}
                                      disabled={replacingSlot !== null || deletingSlot !== null}
                                      onPick={(f) => handleReplaceImage(slot, f)}
                                      onError={setError}
                                      sx={outlinedYellowSx}
                                    />
                                    <MediaDeleteButton
                                      tooltip="Remove image"
                                      busy={deletingSlot === slot}
                                      disabled={replacingSlot !== null || deletingSlot !== null}
                                      onClick={() =>
                                        setConfirmDelete({ kind: 'image', slot, label: slotLabel(slot) })
                                      }
                                    />
                                  </Stack>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Box key={slot} sx={{ display: 'flex', flexDirection: 'column' }}>
                              <AddImageDropzone
                                label="Add image"
                                height={{ xs: 150, sm: 170 }}
                                busy={replacingSlot === slot}
                                disabled={replacingSlot !== null || deletingSlot !== null}
                                onPick={(f) => handleReplaceImage(slot, f)}
                                onError={setError}
                              />
                              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, px: 0.25 }}>
                                {slotLabel(slot)}
                              </Typography>
                            </Box>
                          ),
                        )}
                        {strayImages.map((image) => (
                          <Box key={image.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                component="img"
                                src={image.url}
                                alt={image.credit ? `© ${image.credit}` : 'image'}
                                loading="lazy"
                                onClick={() => setLightbox(image)}
                                sx={{
                                  width: '100%',
                                  height: { xs: 150, sm: 170 },
                                  objectFit: 'contain',
                                  bgcolor: 'background.default',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  display: 'block',
                                  cursor: 'zoom-in',
                                }}
                              />
                              {canDownloadImages && (
                                <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                                  <ImageDownloadButton
                                    onClick={() => handleDownloadImage(image.id)}
                                    busy={downloading === image.id}
                                    disabled={downloading !== null}
                                  />
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ px: 0.25 }}>
                              <ImageCreditEditor
                                uuid={uuid}
                                img={image}
                                canEdit={canEdit}
                                onSaved={handleCreditSaved}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>
              </Stack>
            )}
          </SectionCard>

          <SectionCard title={`A2 Judging Boards (${a2Boards.length})`}>
            {/* Boards are hosted on our storage now: upload a PDF per slot and the
                "Open A2 board" link repoints at our copy. The old free-text URL
                field is gone — uploads are immediate (no edit-mode gate). */}
            <A2BoardsPreview boards={a2Boards} buttonSx={outlinedYellowSx} />
            {canEdit && (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {([['a2board1', 'A2 Board 1'], ['a2board2', 'A2 Board 2']] as const).map(
                  ([slot, label]) => (
                    <Stack
                      key={slot}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', rowGap: 1 }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                        {label}
                      </Typography>
                      <MediaUploadButton
                        label={nomination[slot] ? 'Replace PDF' : 'Upload PDF'}
                        accept="application/pdf"
                        acceptTypes={['application/pdf']}
                        busy={a2UploadingSlot === slot}
                        disabled={a2UploadingSlot !== null || deletingSlot !== null}
                        onPick={(f) => handleUploadA2Board(slot, f)}
                        onError={setError}
                        sx={outlinedYellowSx}
                      />
                      {nomination[slot] && (
                        <MediaDeleteButton
                          tooltip="Remove A2 board"
                          busy={deletingSlot === slot}
                          disabled={a2UploadingSlot !== null || deletingSlot !== null}
                          onClick={() => setConfirmDelete({ kind: 'a2board', slot, label })}
                        />
                      )}
                    </Stack>
                  ),
                )}
              </Stack>
            )}
          </SectionCard>

          <SectionCard title="Project Video">
            {/* Preview stays put in both view and edit; the editable URL inputs
                are appended below when editing so nothing above them jumps. */}
            <VideoPreview nomination={nomination} buttonSx={outlinedYellowSx} />
            {editing && canEdit && (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {([
                  ['video_landscape_url', 'Landscape video link', false],
                  ['video_portrait_url', 'Portrait video link', false],
                  ['video_download_url', 'Video download link', false],
                  ['video_copyright', 'Video copyright', true],
                ] as const).map(([key, label, multiline]) => (
                  <Stack key={key} spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline={multiline}
                      minRows={multiline ? 2 : undefined}
                      value={(draft[key] as string) ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard title="Presentation Files">
            <Typography variant="body2" color="text.secondary">
              No files uploaded. (Upload workflow coming in a later sprint.)
            </Typography>
          </SectionCard>

          {canEdit && (
          <SectionCard title={`History (${auditLog.length})`}>
            {auditLog.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No changes recorded yet. Edits, Evessio syncs and approved change requests will show up here.
              </Typography>
            ) : (
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {auditLog.slice(0, 20).map((entry) => (
                  <Box key={entry.id}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="baseline"
                      sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {prettyField(entry.field)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {relativeTime(entry.created_at)}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                        columnGap: 2,
                        rowGap: 0.25,
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">From</Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.disabled',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatAuditValue(entry.value_before)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">To</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {formatAuditValue(entry.value_after)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {entry.actor} · {prettySource(entry.source)} · {new Date(entry.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
            {auditLog.length > 20 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Showing the 20 most recent of {auditLog.length} total changes.
              </Typography>
            )}
          </SectionCard>
          )}
        </>
      )}

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>
          {confirmDelete?.kind === 'a2board' ? 'Remove this A2 board?' : 'Remove this image?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete?.kind === 'a2board'
              ? `The ${confirmDelete?.label} PDF will be removed from this entry.`
              : `The ${confirmDelete?.label ?? 'image'} will be archived and cleared from the entry.` +
                (confirmDelete?.slot === 'hero' ? ' This is the main image for the entry.' : '')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runConfirmedDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Click-to-enlarge lightbox — shows the full image (contain, no crop) so
          tall/panoramic entries can be inspected in full. Click anywhere to close. */}
      <Dialog open={lightbox !== null} onClose={() => setLightbox(null)} maxWidth="lg">
        {lightbox && (
          <Box
            component="img"
            src={lightbox.url}
            alt={lightbox.credit ? `© ${lightbox.credit}` : 'Enlarged image'}
            onClick={() => setLightbox(null)}
            sx={{
              display: 'block',
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              cursor: 'zoom-out',
            }}
          />
        )}
      </Dialog>
    </Stack>
  );
}
